import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Award, GraduationCap, Star, Users, Ticket, Sparkles, Copy, Gift, Zap, Loader2, ArrowLeft, Trophy, Bell, BellOff, Pencil } from "lucide-react";
import { getMyCommunity, exchangeRewards, getLeaderboard, setPreferences } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MyAELogo } from "../components/myae/Logo";

/**
 * /community — MyAE Community Profile
 * Points · Verified grades · Feedback · Referrals · Prize-draw entries · Share link · Exchange rewards
 */
export default function Community() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);   // 'credits' | 'pro_days' | null
  const [board, setBoard] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [reminders, setReminders] = useState(true);   // subscribed by default
  const [savingPrefs, setSavingPrefs] = useState(false);

  const refresh = () => {
    getMyCommunity().then(setData).catch(() => {});
    getLeaderboard().then(setBoard).catch(() => {});
  };
  useEffect(() => { if (user) refresh(); }, [user?.user_id]);
  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name || "");
      setReminders(!user.reminders_opted_out);
    }
  }, [user?.user_id]);

  const doExchange = async (kind) => {
    setBusy(kind);
    try {
      const r = await exchangeRewards(kind);
      toast.success(kind === "credits"
        ? `Exchanged for ${r.amount} credit${r.amount > 1 ? "s" : ""}.`
        : `Exchanged for ${r.amount} Pro days.`);
      setData((d) => ({ ...(d || {}), ...(r.community || {}) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Exchange failed.");
    } finally { setBusy(null); }
  };

  const savePrefs = async (patch) => {
    setSavingPrefs(true);
    try {
      await setPreferences(patch);
      toast.success("Preferences updated.");
      getLeaderboard().then(setBoard).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setSavingPrefs(false); }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <p className="font-serif text-2xl mb-2">MyAE Community</p>
          <p className="text-sm text-zinc-600 mb-4">Sign in to earn Community Points, refer friends, and enter monthly prize draws.</p>
          <Link to="/login" className="rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-medium hover:bg-indigo-700">Sign in</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center gap-3 text-zinc-500 bg-zinc-50"><Loader2 className="animate-spin" /> Loading…</div>;
  }

  const cfg = data.rewards_config || {};
  const canCredits = data.points >= (cfg.exchange_credits_cost || 0);
  const canPro = data.points >= (cfg.exchange_pro_days_cost || 0);

  const stat = (label, value, sub, Icon, testid) => (
    <div className="bg-white p-4 border border-zinc-200 rounded-xl" data-testid={testid}>
      <div className="flex items-start justify-between mb-1">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
        {Icon && <Icon size={14} className="text-indigo-500" />}
      </div>
      <p className="font-serif text-2xl text-zinc-900">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-0.5">{sub}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-between mb-8">
          <Link to="/dashboard" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"><ArrowLeft size={14} /> Dashboard</Link>
          <MyAELogo />
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Hero */}
          <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 text-white p-8 mb-6" data-testid="community-hero">
            <div className="flex items-center gap-3 mb-4">
              <Award size={28} />
              <p className="font-serif text-3xl">MyAE Community</p>
            </div>
            <p className="text-indigo-100 mb-6">Every verified contribution helps improve MyAE for future students — and earns you rewards.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-indigo-200">Community Points</p>
                <p className="font-serif text-4xl" data-testid="community-points">{data.points}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-indigo-200">Prize entries this month</p>
                <p className="font-serif text-4xl" data-testid="community-entries-month">{data.entries_this_month}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-indigo-200">Total entries</p>
                <p className="font-serif text-4xl">{data.total_entries}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-indigo-200">Referrals</p>
                <p className="font-serif text-4xl" data-testid="community-referrals">{data.referrals}</p>
              </div>
            </div>
          </div>

          {/* Contribution breakdown */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {stat("Verified grades", data.verified_grades, `+${cfg.points_grade_confirm} pts each`, GraduationCap, "community-verified-grades")}
            {stat("Feedback submitted", data.feedback_count, `+${cfg.points_feedback} pts each`, Star, "community-feedback")}
            {stat("Referrals", data.referrals, `+${cfg.points_referral} pts each`, Users, "community-referrals-card")}
            {stat("Prize entries", data.total_entries, "1 per contribution", Ticket, "community-entries-card")}
          </div>

          {/* Referral share */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-6" data-testid="community-referral-block">
            <div className="flex items-start gap-3 mb-3">
              <Sparkles className="text-indigo-500" size={20} />
              <div>
                <p className="font-serif text-lg">Invite friends — you both win</p>
                <p className="text-sm text-zinc-600">Share your personal link. When a friend signs up, you each get bonus credits + you earn {cfg.points_referral} points.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <input readOnly value={data.referral_link} data-testid="community-referral-link"
                className="flex-1 min-w-0 font-mono text-xs rounded-lg border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
              <button onClick={() => { navigator.clipboard?.writeText(data.referral_link); toast.success("Referral link copied."); }}
                data-testid="community-copy-referral"
                className="inline-flex items-center gap-1 rounded-lg border border-zinc-300 bg-white px-3 py-2 text-xs font-medium hover:border-indigo-500 hover:text-indigo-600">
                <Copy size={12} /> Copy
              </button>
            </div>
          </div>

          {/* Rewards exchange */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-6" data-testid="community-exchange">
            <p className="font-serif text-lg mb-1 flex items-center gap-2"><Gift size={16} className="text-indigo-500" /> Redeem your points</p>
            <p className="text-sm text-zinc-600 mb-4">Turn Community Points into MyAE benefits.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className={`border rounded-xl p-4 ${canCredits ? "border-indigo-300 bg-indigo-50/40" : "border-zinc-200 bg-zinc-50"}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-zinc-900">Analysis credits</p>
                  <Zap size={16} className="text-indigo-500" />
                </div>
                <p className="text-sm text-zinc-600 mb-3">{cfg.exchange_credits_amount} credit for {cfg.exchange_credits_cost} points</p>
                <button onClick={() => doExchange("credits")} disabled={!canCredits || busy}
                  data-testid="community-exchange-credits"
                  className="w-full rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2">
                  {busy === "credits" ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                  {canCredits ? "Redeem" : `Need ${(cfg.exchange_credits_cost || 0) - data.points} more`}
                </button>
              </div>
              <div className={`border rounded-xl p-4 ${canPro ? "border-purple-300 bg-purple-50/40" : "border-zinc-200 bg-zinc-50"}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-zinc-900">Pro subscription days</p>
                  <Award size={16} className="text-purple-500" />
                </div>
                <p className="text-sm text-zinc-600 mb-3">{cfg.exchange_pro_days_amount} days of Pro for {cfg.exchange_pro_days_cost} points</p>
                <button onClick={() => doExchange("pro_days")} disabled={!canPro || busy}
                  data-testid="community-exchange-pro"
                  className="w-full rounded-lg bg-purple-600 text-white px-4 py-2 text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2">
                  {busy === "pro_days" ? <Loader2 size={14} className="animate-spin" /> : <Award size={14} />}
                  {canPro ? "Redeem" : `Need ${(cfg.exchange_pro_days_cost || 0) - data.points} more`}
                </button>
              </div>
            </div>
          </div>

          {/* How to earn */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6" data-testid="community-earn">
            <p className="font-serif text-lg mb-3">How to earn points</p>
            <ul className="space-y-2 text-sm text-zinc-700">
              <li className="flex items-center gap-3"><GraduationCap size={16} className="text-emerald-500" /> <span>Confirm your university grade <b className="text-zinc-900">+{cfg.points_grade_confirm} pts</b></span></li>
              <li className="flex items-center gap-3"><Star size={16} className="text-amber-500" /> <span>Rate a report / share improvement feedback <b className="text-zinc-900">+{cfg.points_feedback} pts</b></span></li>
              <li className="flex items-center gap-3"><Users size={16} className="text-indigo-500" /> <span>Refer a friend who signs up <b className="text-zinc-900">+{cfg.points_referral} pts</b></span></li>
              <li className="flex items-center gap-3"><Sparkles size={16} className="text-purple-500" /> <span>Complete your profile (name, university) <b className="text-zinc-900">+{cfg.points_profile_complete} pts</b></span></li>
            </ul>
            <p className="text-xs text-zinc-500 mt-4">Every verified contribution earns 1 monthly prize-draw entry. Grades are fully anonymised before contributing to calibration — your identity never enters the calibration dataset.</p>
          </div>

          {/* --- Referral Leaderboard --- */}
          {board && (
            <div className="bg-white border border-zinc-200 rounded-xl p-6 mt-6" data-testid="community-leaderboard">
              <div className="flex items-center gap-2 mb-1">
                <Trophy size={18} className="text-amber-500" />
                <p className="font-serif text-lg">Top referrers this month</p>
              </div>
              <p className="text-xs text-zinc-500 mb-4">{board.explanation}</p>
              {board.leaderboard.length === 0 ? (
                <p className="text-sm text-zinc-500 italic">No referrals yet this month — be the first!</p>
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {board.leaderboard.map((row) => {
                    const badgeColor = row.badge === "gold" ? "bg-amber-100 text-amber-800 border-amber-300"
                      : row.badge === "silver" ? "bg-zinc-100 text-zinc-700 border-zinc-300"
                      : row.badge === "bronze" ? "bg-orange-100 text-orange-800 border-orange-300"
                      : "bg-indigo-50 text-indigo-700 border-indigo-200";
                    const medal = row.badge === "gold" ? "🥇" : row.badge === "silver" ? "🥈" : row.badge === "bronze" ? "🥉" : `#${row.rank}`;
                    return (
                      <li key={row.rank} className={`flex items-center justify-between py-3 ${row.is_me ? "bg-indigo-50/50 -mx-2 px-2 rounded-lg" : ""}`} data-testid={`leaderboard-row-${row.rank}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-lg w-10 shrink-0">{medal}</span>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-zinc-900 truncate">
                              {row.display_name} {row.is_me && <span className="text-xs text-indigo-600 ml-1">(you)</span>}
                            </p>
                            <p className="text-xs text-zinc-500">{row.community_points} community points</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`inline-flex items-center gap-1 border px-2 py-0.5 rounded-full text-[11px] font-medium ${badgeColor}`}>
                            {row.badge.toUpperCase()}
                          </span>
                          <span className="font-mono text-sm text-zinc-900 w-16 text-right">{row.referrals}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {/* --- Preferences --- */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6 mt-6" data-testid="community-preferences">
            <p className="font-serif text-lg mb-3 flex items-center gap-2"><Pencil size={16} className="text-zinc-500" /> Preferences</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Display name on leaderboard</label>
                <div className="flex items-center gap-2">
                  <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Optional — anonymous by default"
                    data-testid="community-display-name"
                    maxLength={40}
                    className="flex-1 rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  <button onClick={() => savePrefs({ display_name: displayName })} disabled={savingPrefs}
                    data-testid="community-save-display-name"
                    className="rounded-lg bg-indigo-600 text-white px-3 py-2 text-sm font-medium hover:bg-indigo-700 disabled:opacity-60">
                    Save
                  </button>
                </div>
                <p className="text-xs text-zinc-500 mt-1">Leave blank to show as &ldquo;First name + last initial&rdquo;.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Grade-confirmation reminders</label>
                <button onClick={() => { const next = !reminders; setReminders(next); savePrefs({ reminders_opted_out: !next }); }}
                  data-testid="community-toggle-reminders"
                  disabled={savingPrefs}
                  className={`w-full inline-flex items-center gap-2 justify-center rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${reminders ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-zinc-300 bg-zinc-50 text-zinc-700 hover:bg-zinc-100"}`}>
                  {reminders ? <><Bell size={14} /> Reminders ON — one nudge 14 days after each analysis</> : <><BellOff size={14} /> Reminders OFF — click to turn on</>}
                </button>
                <p className="text-xs text-zinc-500 mt-1">Only one reminder is ever sent per analysis.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
